import { Clipboard } from '@angular/cdk/clipboard';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { v4 as uuid } from 'uuid';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { HttpContextTokens, LogLevel } from '../constants';
import { ILogging } from '../interfaces/config.interface';
import { ErrorReportingProvider } from '../interfaces/error-reporting-provider.interface';
import { HttpLoggingOptions } from '../models/http-logging-options.model';
import { AppConfigService } from './app-config.service';

const sessionCorrelationId = 'logging-correlation-id';
@Injectable({
  providedIn: 'root',
})
export class ErrorReportingService implements ErrorReportingProvider {
  private minimumLogLevel: LogLevel = LogLevel.NEVER;
  private consoleLogging: boolean = false;
  private loggingConfig: ILogging;
  private _correlationId: string;
  private enableConsoleLoggingSecret: string = '2A360609-3084-493E-AD68-7483D42013F0';
  private httpContext: HttpContext;

  constructor(
    private httpClient: HttpClient,
    private snackbar: MatSnackBar,
    private clipboard: Clipboard,
    private env: AppConfigService
  ) {
    //The config may not always be immediatly available as it is loaded through HTTP which injects this service
    //So we can try and load it now, and also at the point where it is first needed (it will only be loaded once)
    this.loadConfig();

    this.initialiseHttpContext();
  }

  logTrace(message: string, source: string = null): void {
    if (!this.loggingConfig) this.loadConfig();
    if (this.consoleLogging) console.trace(`[${this.correlationId}] : ${message}`);
    this.log(LogLevel.TRACE, message, source);
  }

  logDebug(message: string, source: string = null): void {
    if (!this.loggingConfig) this.loadConfig();
    if (this.consoleLogging) console.debug(`[${this.correlationId}] : ${message}`);
    this.log(LogLevel.DEBUG, message, source);
  }

  logInfo(message: string, userMessage: string = null, source: string = null): void {
    if (!this.loggingConfig) this.loadConfig();
    if (this.consoleLogging) console.info(`[${this.correlationId}] : ${message}`);
    this.log(LogLevel.INFO, message, source);
    if (userMessage !== null) this.displayInfo(userMessage);
  }

  logWarning(message: string, userMessage: string = null, source: string = null): void {
    if (!this.loggingConfig) this.loadConfig();
    if (userMessage !== null) userMessage = message;
    if (this.consoleLogging) console.warn(`[${this.correlationId}] : ${message}`);
    this.log(LogLevel.WARNING, message, source);
    this.displayWarning(userMessage);
  }

  logError(message: any, userMessage: string = null, source: string = null): void {
    if (!this.loggingConfig) this.loadConfig();
    if (userMessage !== null) userMessage = message;
    let messageString: string = this.getMessageString(message);
    if (this.consoleLogging) console.error(`[${this.correlationId}] : ${messageString}`);
    this.log(LogLevel.ERROR, messageString, source);
    this.display(LogLevel.ERROR, userMessage, true, null);
  }

  logCritical(message: any, userMessage: string = null, source: string = null): void {
    if (!this.loggingConfig) this.loadConfig();
    if (userMessage !== null) userMessage = message;
    let messageString: string = this.getMessageString(message);
    if (this.consoleLogging) console.error(`[${this.correlationId}] : ${messageString}`);
    this.log(LogLevel.CRITICAL, messageString, source);
    this.display(LogLevel.CRITICAL, userMessage, true, null);
  }

  getResponseDTOErrors(response: HttpErrorResponse): string[] {
    const errorList: string[] = [];
    if (response.status >= 400 && response?.error?.errors) {
      for (const err in response.error.errors) {
        const error = response.error.errors[err];
        let messages = '';
        if (typeof error === 'string') {
          messages = response.error.errors[err];
        } else {
          messages = response.error.errors[err].map((message) => {
            return `${err}: ${message}`;
          });
        }
        errorList.push(messages);
      }
    } else {
      errorList.push(`${response.status}: ${response.statusText}`);
    }
    return errorList;
  }

  private log(level: LogLevel, message: string, source: string = null): void {
    if (!this.minimumLogLevel || level >= this.minimumLogLevel) {
      this.httpClient
        .post(
          `${this.env.config.gatewayUrl}/Log/Message`,
          {
            level: level,
            message: message,
            uuid: this.correlationId,
            source: source,
          },
          {
            context: this.httpContext,
          }
        )
        .subscribe();
    }
  }

  private getMessageString(errorMessage: any) {
    let messageString: string = '';
    if (typeof errorMessage === 'string') {
      messageString = errorMessage;
    } else if (errorMessage && errorMessage.message) {
      //This is an error object (probably from the catch on a promise)
      messageString = errorMessage.message;
    } else {
      //we can just try toString()
      messageString = errorMessage.toString();
    }
    return messageString;
  }

  //These methods only display to the user, there is no display method for Error or Critical because we want to log those
  //back ot the API
  displayInfo(message: string, showCopy: boolean = false): void {
    this.display(LogLevel.INFO, message, showCopy);
  }

  displayWarning(message: string, showCopy: boolean = false): void {
    this.display(LogLevel.WARNING, message, showCopy);
  }

  private display(level: LogLevel, message: string, showCopy: boolean, duration: number = 2500): void {
    let snackConfig: MatSnackBarConfig = {
      panelClass: `user-notification--${LogLevel[level].toLowerCase()}`,
      duration: duration,
    };
    let snack = this.snackbar.openFromComponent(PopupMessageComponent, {
      data: {
        message: message,
        uuid: this.correlationId,
        showCopy: showCopy,
        isError: level > LogLevel.WARNING,
      },
      ...snackConfig,
    });
    if (showCopy) {
      snack.onAction().subscribe(() => {
        this.clipboard.copy(`[${this.correlationId}] : ${message}`);
      });
    }
  }

  private get correlationId(): string {
    if (!this._correlationId) {
      //Guid has not bee previously loaded so check for a value in session storage
      var storedGuid = sessionStorage.getItem(sessionCorrelationId);
      if (storedGuid) {
        this._correlationId = storedGuid;
      } else {
        //Nothing in session storage either, so create a new one.
        this._correlationId = uuid();
        sessionStorage.setItem(sessionCorrelationId, this._correlationId);
      }
    }
    return this._correlationId;
  }

  private loadConfig() {
    if (!this.loggingConfig) {
      if (this.env?.config?.logging) {
        this.loggingConfig = this.env.config.logging;
        if (this.loggingConfig.minimumLogLevel) {
          this.minimumLogLevel = LogLevel[this.loggingConfig.minimumLogLevel.toUpperCase()];
        }
        this.consoleLogging = this.loggingConfig.consoleLogging && this.loggingConfig.consoleLogging === this.enableConsoleLoggingSecret;
      }
    }
  }

  private initialiseHttpContext() {
    this.httpContext = new HttpContext();

    var httpLoggingOptions = new HttpLoggingOptions();
    //Because making a call to the httpClient will automatically log the data through this service, we need to disable logging for all
    //calls to http client in here.
    httpLoggingOptions.doNotLog = true;

    this.httpContext.set(HttpContextTokens.options, httpLoggingOptions);
  }
}
